import React, { useEffect, useState, useRef } from 'react';
import {
    Box,
    Button,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
    Chip,
} from '@mui/material';
import {
    Search as SearchIcon,
    GetApp as DownloadIcon,
    FilterList as FilterIcon,
    Sort as SortIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { CustomPagination } from '../../../Tables/Pagination';
import { IPagination } from '../../../../hook/usePagination';
import _ from 'lodash';
import { IManagedEventTicket } from '../../../../redux/events/types';
import { ColumnsData, Table } from '../../../Tables/Table';

interface EventTicketsTabProps {
    tickets: IManagedEventTicket[];
    pagination: IPagination;
    onSearchChange: (searchText: string) => void;
    onTicketTypeChange: (type: string) => void;
    onSortOrderChange: (order: 'asc' | 'desc') => void;
    onRowsChange: (rows: number) => void;
    onPageChange: (page: number) => void;
    onClearSearch: () => void;
    onClearTicketType?: () => void;
    onExportCSV: (tickets?: IManagedEventTicket[]) => void;
}

// Sort options
const SORT_OPTIONS = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'price_high', label: 'Price (High to Low)' },
    { value: 'price_low', label: 'Price (Low to High)' },
];

// Ticket type options
const TICKET_TYPE_OPTIONS = [
    { value: 'all', label: 'All Tickets' },
    { value: 'checked_in', label: 'Checked In' },
    { value: 'not_checked_in', label: 'Not Checked In' },
];

export const EventTicketsTab: React.FC<EventTicketsTabProps> = ({
    tickets = [],
    pagination,
    onSearchChange,
    onTicketTypeChange,
    onSortOrderChange,
    onRowsChange,
    onPageChange,
    onClearSearch,
    onClearTicketType,
    onExportCSV,
}) => {
    const { themeStyle } = useThemeContext();
    const [searchValue, setSearchValue] = useState('');
    const [sortOption, setSortOption] = useState('newest');
    const [ticketTypeFilter, setTicketTypeFilter] = useState('all');
    const [showFilters, setShowFilters] = useState(false);

    // Create debounced search handler
    const debouncedSearchHandler = useRef(
        _.debounce((searchText: string) => {
            if (onSearchChange) {
                onSearchChange(searchText);
            }
        }, 500),
    ).current;

    // Clean up debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedSearchHandler.cancel();
        };
    }, [debouncedSearchHandler]);

    // Handle search input change
    const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setSearchValue(newValue);
        debouncedSearchHandler(newValue);
    };

    // Handle clear search
    const handleClearSearch = () => {
        setSearchValue('');
        onClearSearch();
    };

    // Handle sort change
    const handleSortChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setSortOption(value);

        if (value === 'newest') {
            onSortOrderChange('desc');
        } else if (value === 'oldest') {
            onSortOrderChange('asc');
        } else if (value === 'price_high') {
            onSortOrderChange('desc');
        } else if (value === 'price_low') {
            onSortOrderChange('asc');
        }
    };

    // Handle ticket type filter change
    const handleTicketTypeChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setTicketTypeFilter(value);
        onTicketTypeChange(value);
    };

    const handleClearTicketTypeFilter = () => {
        setTicketTypeFilter('all');
        if (onClearTicketType) {
            onClearTicketType();
        } else {
            onTicketTypeChange('all');
        }
    };

    // Table columns
    const columnsData: ColumnsData[] = [
        {
            field: 'ticket_id',
            label: 'Ticket ID',
            type: 'text',
            sortable: true,
            flex: 1,
        },
        {
            field: 'recipient_email',
            label: 'Recipient',
            type: 'text',
            sortable: true,
            width: 180,
        },
        {
            field: 'ticket_type',
            label: 'Type',
            type: 'text',
            sortable: true,
            width: 120,
        },
        {
            field: 'formatted_price',
            label: 'Price',
            type: 'text',
            sortable: true,
            width: 100,
        },
        {
            field: 'checked_in',
            label: 'Status',
            type: 'custom',
            sortable: false,
            customCell: (params: IManagedEventTicket) => (
                <Chip
                    label={params.checked_in ? 'Checked In' : 'Not Checked In'}
                    size="small"
                    sx={{
                        backgroundColor: params.checked_in
                            ? theme[themeStyle].primaryGreen
                            : theme[themeStyle].highlight,
                        color: params.checked_in ? theme[themeStyle].primary_white : theme[themeStyle].black,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: '0.75rem',
                    }}
                />
            ),
            width: 200,
        },
        {
            field: 'actions',
            label: 'Actions',
            type: 'custom',
            sortable: false,
            customCell: (params: IManagedEventTicket) => (
                <Button
                    variant="outlined"
                    size="small"
                    sx={{
                        textTransform: 'none',
                        borderColor: theme[themeStyle].primary_orange,
                        color: theme[themeStyle].primary_white,
                        '&:hover': {
                            borderColor: theme[themeStyle].primary_orange,
                            backgroundColor: 'rgba(255, 166, 0, 0.1)',
                        },
                    }}>
                    {params.checked_in ? 'View Details' : 'Check In'}
                </Button>
            ),
            flex: 1,
            width: '100%',
        },
    ];

    return (
        <Box>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: '1.25rem',
                    color: theme[themeStyle].primary_white,
                    mb: 3,
                }}>
                Tickets
            </Typography>

            {/* Tickets Summary */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <Box
                        sx={{
                            backgroundColor: theme[themeStyle].table_header,
                            borderRadius: 2,
                            padding: 2,
                            height: '100%',
                        }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                fontSize: '0.875rem',
                                color: theme[themeStyle].light_gray_text,
                            }}>
                            Total Tickets
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: '1.75rem',
                                color: theme[themeStyle].primary_white,
                                mt: 0.5,
                            }}>
                            {pagination?.totalCount || 0}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Box
                        sx={{
                            backgroundColor: theme[themeStyle].table_header,
                            borderRadius: 2,
                            padding: 2,
                            height: '100%',
                        }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                fontSize: '0.875rem',
                                color: theme[themeStyle].light_gray_text,
                            }}>
                            Checked In
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: '1.75rem',
                                color: theme[themeStyle].primary_white,
                                mt: 0.5,
                            }}>
                            {tickets.filter(ticket => ticket.checked_in).length}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Box
                        sx={{
                            backgroundColor: theme[themeStyle].table_header,
                            borderRadius: 2,
                            padding: 2,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                        }}>
                        <Button
                            startIcon={<DownloadIcon />}
                            onClick={() => onExportCSV(tickets)}
                            sx={{
                                color: theme[themeStyle].black,
                                backgroundColor: theme[themeStyle].highlight,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                '&:hover': {
                                    backgroundColor: theme[themeStyle].primary_orange,
                                    opacity: 0.9,
                                },
                                py: 1.5,
                            }}>
                            Export Tickets as CSV
                        </Button>
                    </Box>
                </Grid>
            </Grid>

            {/* Search and Filter Controls */}
            <Box sx={{ mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <TextField
                            placeholder="Search by name, email or ticket ID"
                            value={searchValue}
                            onChange={handleSearchInputChange}
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: theme[themeStyle].tag_white }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchValue ? (
                                    <InputAdornment position="end">
                                        <CloseIcon
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                cursor: 'pointer',
                                            }}
                                            onClick={handleClearSearch}
                                        />
                                    </InputAdornment>
                                ) : null,
                                sx: {
                                    backgroundColor: theme[themeStyle].table_header,
                                    borderRadius: 2,
                                    color: theme[themeStyle].primary_white,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].placeholderGray50,
                                    },
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button
                                startIcon={<FilterIcon />}
                                onClick={() => setShowFilters(!showFilters)}
                                sx={{
                                    color: theme[themeStyle].primary_white,
                                    borderColor: theme[themeStyle].placeholderGray,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    border: `1px solid ${theme[themeStyle].placeholderGray}`,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    backgroundColor: showFilters ? theme[themeStyle].primary_orange : 'transparent',
                                    '&:hover': {
                                        backgroundColor: theme[themeStyle].primary_orange,
                                        opacity: 0.9,
                                    },
                                }}>
                                Filters
                            </Button>

                            <FormControl
                                sx={{
                                    minWidth: 150,
                                    '& .MuiInputBase-root': {
                                        backgroundColor: theme[themeStyle].table_header,
                                        color: theme[themeStyle].primary_white,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        borderRadius: 2,
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].placeholderGray50,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: theme[themeStyle].primary_white,
                                    },
                                }}>
                                <InputLabel id="sort-select-label" sx={{ color: theme[themeStyle].gray_text }}>
                                    Sort By
                                </InputLabel>
                                <Select
                                    labelId="sort-select-label"
                                    value={sortOption}
                                    onChange={handleSortChange}
                                    label="Sort By"
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <SortIcon sx={{ color: theme[themeStyle].tag_white }} />
                                        </InputAdornment>
                                    }>
                                    {SORT_OPTIONS.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Ticket Type Filter */}
            {showFilters && (
                <Box
                    sx={{
                        mb: 3,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: theme[themeStyle].table_header,
                    }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={5}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '0.875rem',
                                    mb: 1,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                Ticket Type
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={ticketTypeFilter}
                                    onChange={handleTicketTypeChange}
                                    displayEmpty
                                    sx={{
                                        backgroundColor: theme[themeStyle].dropdown_black,
                                        color: theme[themeStyle].primary_white,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    }}>
                                    {TICKET_TYPE_OPTIONS.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={7}>
                            <Box
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                }}>
                                <Button
                                    onClick={handleClearTicketTypeFilter}
                                    sx={{
                                        color: theme[themeStyle].primary_white,
                                        borderColor: theme[themeStyle].placeholderGray,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        border: `1px solid ${theme[themeStyle].placeholderGray}`,
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                            opacity: 0.8,
                                        },
                                    }}>
                                    Clear Filters
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* Table */}
            <Table
                columns={columnsData}
                staticData={tickets}
                rowsPerPageOptions={[5, 10, 25, 50]}
                tableType="tickets"
                rowHeight={60}
                headerHeight={50}
                textSx={{ fontSize: 13.5 }}
                // Add pagination, sorting, and other props as needed
            />

            {/* Pagination */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <CustomPagination
                    pagination={{
                        page: pagination.page,
                        rows: pagination.rows,
                        totalCount: pagination.totalCount,
                    }}
                    onPageChange={onPageChange}
                    onRowsChange={onRowsChange}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            </Box>
        </Box>
    );
};
